import { useEffect, useRef, useState } from "react";
import _isEmpty from "lodash/isEmpty";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import TrashIcon from "@assets/icons/trash.svg";

import useUserStore from "@store/useUserStore";
import useDialogStore from "@store/useDialogStore";
import { useGetBoundNote } from "@components/Sections/NoteSection/hooks/useGetBoundNote";
import { useMeetingObjects } from "@screens/meeting/hooks/useMeetingObjects";
import { useRemoveMeeting } from "@screens/meeting/hooks/useRemoveMeeting";
import { useUpdateMeeting } from "@screens/meeting/hooks/useUpdateMeeting";
import { MeetingSubtype, MeetingType } from "@enums/meetings.enum";
import { RelationObjectType } from "@enums/relations.enum";
import { IMeeting, IMeetingUpdatePayload } from "@interfaces/meeting.interface";
import { MeetingPrivacySwitcher, MeetingSubtypeButtonSelector } from "@components/Meetings";
import { RemoveMeetingDialog } from "@components/Meetings";
import { MeetingInfo } from "@components/Meetings";
import { DisconnectedError } from "@app-types/errors";
import { Button } from "@ui/Button";
import { View } from "@ui/View";

interface Props {
  meeting?: IMeeting;
  isNewlySynced?: boolean;
  hasTagsOrTopics?: boolean;
  isRecurringSeries?: boolean;
}

export function MeetingHeader({ meeting, isNewlySynced, hasTagsOrTopics, isRecurringSeries }: Props) {
  const { track } = useUserpilot();
  const { activeUser } = useUserStore();
  const { openDialog, closeDialog } = useDialogStore();
  const { mutate: removeMeetingMutate } = useRemoveMeeting(closeDialog);
  const { mutate: updateMeetingMutate } = useUpdateMeeting(() => {
    setLocalMeetingSubtype(prevMeetingSubtype.current);
  });
  const { refetch: refetchMeetingObjects } = useMeetingObjects(false);
  const { refetch: refetchBoundNote } = useGetBoundNote(
    meeting?.id,
    RelationObjectType.MEETING,
    false
  );

  const [localMeetingSubtype, setLocalMeetingSubtype] = useState<MeetingSubtype | undefined>(
    meeting?.subtype
  );
  const prevMeetingSubtype = useRef<MeetingSubtype | undefined>(meeting?.subtype);

  const isDisabled = meeting && meeting.created_by?.id !== activeUser?.id;
  const isSharedNotesEnabled = activeUser?.organization?.shared_meeting_notes ?? false;

  useEffect(() => {
    setLocalMeetingSubtype(meeting?.subtype);
    prevMeetingSubtype.current = meeting?.subtype;
  }, [meeting?.subtype]);

  const removeMeeting = (topicIds: string[]) => {
    removeMeetingMutate(topicIds);
    track("Click: Remove meeting");
  };

  const updateMeeting = (data: Partial<IMeetingUpdatePayload>) => {
    if (data.subtype !== undefined) {
      if (data.subtype === localMeetingSubtype) return;
      setLocalMeetingSubtype(data.subtype as MeetingSubtype);
    }

    updateMeetingMutate({ payload: { id: meeting.id, ...data } });
  };

  const handleRemoveMeeting = async () => {
    try {
      // Always refetch meeting objects because:
      // - if isNewlySynced is true, we need to check for new objects created after the last sync;
      // - if isNewlySynced is false, we need to fetch meeting objects to be displayed in the Remove Meeting dialog.
      const { data: meetingObjects } = await refetchMeetingObjects();

      const hasMeetingObjects = meetingObjects?.length > 0;
      let hasNotes = false;

      if (isNewlySynced) {
        const { data: note } = await refetchBoundNote();
        hasNotes = note?.content && !_isEmpty(note.content);
      }

      // Show a Remove Meeting dialog if:
      // a) meeting.newly_synced == false (set by the server if the meeting had data to preserve during the last sync);
      // b) the meeting has notes;
      // c) the meeting has tags or discussed topics;
      // d) the meeting has objects (created during the meeting creation)
      if (!isNewlySynced || hasNotes || hasTagsOrTopics || hasMeetingObjects) {
        openDialog(
          <RemoveMeetingDialog
            meeting={meeting}
            meetingObjects={meetingObjects}
            onSubmit={removeMeeting}
          />
        );
      } else {
        removeMeeting([]);
      }
    } catch (error) {
      if (!(error instanceof DisconnectedError)) throw error;
    }
  };

  const justificationClass =
    meeting?.type === MeetingType.MEETING ? "justify-between" : "justify-end";

  return (
    <header className="flex flex-col gap-4 pt-2 w-full">
      <View.Condition if={!isRecurringSeries}>
        <div className={`flex items-center ${justificationClass} gap-1`}>
          <View.Condition if={meeting?.type === MeetingType.MEETING}>
            <MeetingSubtypeButtonSelector
              meetingSubtype={localMeetingSubtype}
              onUpdateMeeting={updateMeeting}
            />
          </View.Condition>

          <div className="flex items-center gap-1">
            <View.Condition if={!isSharedNotesEnabled}>
              <MeetingPrivacySwitcher
                meeting={meeting}
                onUpdateMeeting={updateMeeting}
                isDisabled={isDisabled}
              />
            </View.Condition>

            <Button
              buttonType="SECONDARY"
              onClick={handleRemoveMeeting}
              className="w-[36px] h-[36px] p-0 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80"
              style={!meeting ? { visibility: "hidden" } : undefined}
            >
              <TrashIcon />
            </Button>
          </div>
        </div>
      </View.Condition>

      <MeetingInfo meeting={meeting} isRecurringSeries={isRecurringSeries} />
    </header>
  );
}
